import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { ClassificationService } from '../classification/ClassificationService';
import { listedGovernancePolicies } from './PolicyDerivation';
import { RECORD_LIMIT, loadPolicyView, toClassifiedItem } from './GovernancePolicyData';
import type { ClassifiedItem } from './PolicyVerdict';
import type { PolicyView } from './PolicyTypes';

/**
 * Backs the policy detail drill-in. Loads the live governance policy by id, its real
 * activity summary, and the actual turns it classified over the window (from the
 * interactions OLAP) — each carrying the real verdict value, confidence, and session.
 */
@injectable()
export class PolicyDetailViewModel extends BaseViewModel {
    loading = true;
    loaded = false;
    notFound = false;
    errorMessage: string | null = null;
    view: PolicyView | null = null;
    items: ClassifiedItem[] = [];

    constructor(
        private readonly service: ClassificationService,
        @inject(IAnalyticsGatewayToken) private readonly gateway: IAnalyticsGateway,
    ) {
        super();
    }

    async initialize(policyId: string): Promise<void> {
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            const all = await this.service.list();
            const policy = listedGovernancePolicies(all).find((c) => c.id === policyId);
            if (policy === undefined) {
                this.notFound = true;
                return;
            }
            this.view = await loadPolicyView(this.gateway, policy);
            const fieldName = this.view.fieldName;
            if (fieldName === null) {
                this.items = [];
                return;
            }
            const result = await this.gateway.records({
                source: 'interactions',
                dimensionKeys: [fieldName],
                presentDimensions: [fieldName],
                orderBy: { column: 'timestamp', direction: 'desc' },
                limit: RECORD_LIMIT,
            });
            this.items = result.records.map((record) => toClassifiedItem(record, policy, fieldName));
        } catch (error) {
            this.errorMessage = error instanceof Error ? error.message : 'Failed to load policy detail';
        } finally {
            this.loading = false;
            this.loaded = true;
            this.notifyChange();
        }
    }
}
